import styled from '@emotion/styled';
import { msg } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import type { fabric } from 'fabric';
import type { FabricEditor } from 'modules/fabric-editor';
import {
  applyMatchLayout,
  authoredBundlesBbox,
  collectSurvivingBundles,
  DEFAULT_MATCH_LAYOUT,
  type LayoutScopeKey,
  MatchLayoutAlignment,
  MatchLayoutAxis,
  MatchLayoutSettings,
  MatchLayoutSnapshot,
  revertMatchLayout,
} from 'modules/manager/domain/match-layout';
import { layoutScopeKeyFor } from 'modules/manager/domain/template-type';
import { selectActiveTemplate, setMatchLayoutLocal, updateMatchLayout } from 'modules/manager/store';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import CommitOptionalNumberField from '../shared/commit-optional-number-field';
import HelpBubble from '../shared/help-bubble';
import InspectorRow from '../shared/inspector-row';
import InspectorSection from '../shared/inspector-section';
import SelectField from '../shared/select-field';
import { LAYOUT_SCOPE_DESCRIPTORS } from './layout-scope-descriptors';
import { theme } from 'styles/theme/theme';
import { useDispatchWithToast } from 'store/use-dispatch-with-toast';
import UDCompactCheckbox from 'ud-form/components/compact-checkbox';

interface CommonSlotLayoutSectionProps {
  editor: FabricEditor;
}

const AXIS_OPTIONS = [
  { value: 'auto', label: msg`Авто` },
  { value: 'vertical', label: msg`Вертикально` },
  { value: 'horizontal', label: msg`Горизонтально` },
];

const ALIGNMENT_OPTIONS = [
  { value: 'center', label: msg`Центр` },
  { value: 'start', label: msg`Начало` },
  { value: 'end', label: msg`Конец` },
  { value: 'distribute', label: msg`Распределить` },
];

/**
 * Per-slot layout settings for template types that repeat a bundle — match
 * blocks (MULTIPLE_*), standings rows (TOURNAMENT_TABLE) or player rank slots
 * (TOP_PLAYERS_BY_INDICATORS). Which scope a type uses comes from the
 * template-type registry, and all of the section's Russian copy comes from
 * `LAYOUT_SCOPE_DESCRIPTORS[scopeKey]` — so supporting a new scope is a registry
 * value plus a descriptor entry, not another ternary threaded through the prose.
 */
const CommonSlotLayoutSection: React.FC<CommonSlotLayoutSectionProps> = ({ editor }) => {
  const { t } = useLingui();
  const dispatch = useAppDispatch();
  const dispatchWithToast = useDispatchWithToast();
  const template = useAppSelector(selectActiveTemplate);
  const settings = template?.matchLayout ?? DEFAULT_MATCH_LAYOUT;
  const templateType = editor.placeholders?.getTemplateType();
  // Undefined = the type renders one of everything (SCHEDULE/RESULTS), so there
  // is nothing to lay out and the section hides. The `?? 'matchNumber'` below is
  // inert — it only keeps the hooks unconditional.
  const layoutScopeKey = layoutScopeKeyFor(templateType);
  const scopeKey: LayoutScopeKey = layoutScopeKey ?? 'matchNumber';

  const authoredCount = useMemo(() => countAuthoredSlots(template?.objects ?? [], scopeKey), [template?.objects, scopeKey]);

  const [previewCount, setPreviewCount] = useState<number | undefined>(undefined);
  const previewStateRef = useRef<{ snapshot: MatchLayoutSnapshot; hidden: fabric.Object[] } | null>(null);

  const revertPreview = useCallback(() => {
    const state = previewStateRef.current;
    if (!state) return;
    revertMatchLayout(state.snapshot);
    for (const obj of state.hidden) obj.set({ visible: true });
    previewStateRef.current = null;
    editor.canvas.requestRenderAll();
  }, [editor]);

  const applyPreview = useCallback(
    (count: number) => {
      revertPreview();
      if (!template) return;
      const authoredRegion = authoredBundlesBbox(template.objects ?? [], scopeKey);
      if (!authoredRegion) return;
      editor.canvas.discardActiveObject();
      const hidden: fabric.Object[] = [];
      for (const obj of editor.canvas.getObjects()) {
        const n = (obj.data as Record<string, number | undefined> | undefined)?.[scopeKey];
        if (typeof n === 'number' && n > count) {
          obj.set({ visible: false });
          hidden.push(obj);
        }
      }
      const surviving = collectSurvivingBundles(
        editor.canvas.getObjects().filter((o) => o.visible !== false),
        scopeKey,
      );
      const snapshot = applyMatchLayout({ surviving, authoredRegion, settings });
      previewStateRef.current = { snapshot, hidden };
      editor.canvas.requestRenderAll();
    },
    [editor, settings, template, revertPreview, scopeKey],
  );

  useEffect(() => {
    if (previewCount === undefined || previewCount >= authoredCount || authoredCount === 0) {
      revertPreview();
      return;
    }
    applyPreview(previewCount);
  }, [previewCount, authoredCount, settings, applyPreview, revertPreview]);

  useEffect(() => {
    const off = editor.on('selection:changed', (e) => {
      if (e.active.length > 0 && previewStateRef.current) {
        setPreviewCount(undefined);
      }
    });
    return off;
  }, [editor]);

  useEffect(() => () => revertPreview(), [revertPreview]);

  const commitSettings = useCallback(
    (next: MatchLayoutSettings) => {
      if (!template) return;
      dispatch(setMatchLayoutLocal(next));
      // Раньше провал молчал: на экране — новое значение, на сервере — старое.
      dispatchWithToast(updateMatchLayout({ id: template.id, matchLayout: next }), { error: msg`Не удалось сохранить раскладку` });
    },
    [dispatch, dispatchWithToast, template],
  );

  if (!layoutScopeKey || !template) return null;

  const d = LAYOUT_SCOPE_DESCRIPTORS[layoutScopeKey];
  // Word forms go into whole translated sentences as placeholders, so the
  // translator sees the full sentence and the Russian source stays unchanged.
  const unitPlural = t(d.unitPlural);
  const unitPluralGen = t(d.unitPluralGen);
  const unitPluralCap = t(d.unitPluralCap);
  const unitCountedTwo = t(d.unitCountedTwo);
  const unitSingularPrep = t(d.unitSingularPrep);
  const stackedAdverbial = t(d.stackedAdverbial);
  const autoSource = t(d.autoSource);
  const previewUnitGen = t(d.previewUnitGen);
  const axisOptions = AXIS_OPTIONS.map((option) => ({ value: option.value, label: t(option.label) }));
  const alignmentOptions = ALIGNMENT_OPTIONS.map((option) => ({ value: option.value, label: t(option.label) }));

  const enabledHelp = (
    <span>
      <Trans>
        <b>Выключено</b> (по умолчанию): пустые {unitPlural} скрываются, а оставшиеся {unitPlural} остаются ровно там, где их
        разместили при вёрстке шаблона — если пропали, например, последние {unitPlural}, в конце области останется пустое место.
        <br />
        <br />
        <b>Включено</b>: оставшиеся {unitPlural} дополнительно сдвигаются внутри исходной области (где были нарисованы все {unitPlural}{' '}
        шаблона) по правилам «Ось» и «Выравнивание» ниже — так пустое место убирается или переносится в предсказуемое место.{' '}
        {unitPluralCap} определяются автоматически по {autoSource}, вручную ничего указывать не нужно.
      </Trans>
    </span>
  );

  const axisHelp = (
    <span>
      <Trans>
        Направление, вдоль которого двигаются оставшиеся {unitPlural}, когда «Включено».
        <br />
        <br />
        <b>Авто</b> — направление подбирается автоматически по форме исходной области: если она выше, чем шире, {unitPlural} двигаются
        по вертикали, иначе — по горизонтали. Подходит для большинства случаев.
        <br />
        <b>Вертикально</b> — {unitPlural} двигаются только вверх/вниз, их горизонтальное положение не меняется. Выбирайте, если в
        шаблоне {unitPlural} расположены {stackedAdverbial}.
        <br />
        <b>Горизонтально</b> — {unitPlural} двигаются только влево/вправо, их вертикальное положение не меняется. Выбирайте, если в
        шаблоне {unitPlural} расположены в ряд.
      </Trans>
    </span>
  );

  const alignmentHelp = (
    <span>
      <Trans>
        Где внутри исходной области (вдоль выбранной оси) окажутся оставшиеся {unitPlural} — то есть куда «уедет» пустое место,
        оставшееся от скрытых {unitPluralGen}.
        <br />
        <br />
        <b>Начало</b> — {unitPlural} прижимаются к верхнему (или левому — при горизонтальной оси) краю области, пустое место остаётся в
        конце (снизу/справа).
        <br />
        <b>Конец</b> — {unitPlural} прижимаются к нижнему (или правому) краю области, пустое место остаётся в начале (сверху/слева).
        <br />
        <b>Центр</b> — группа {unitPluralGen} центрируется в области, пустое место делится поровну между началом и концом.
        <br />
        <b>Распределить</b> — {unitPlural} равномерно растягиваются по всей области от края до края, пустого места не остаётся вовсе
        (нужно минимум 2 оставшихся {unitCountedTwo} — при {unitSingularPrep} работает как «Центр»).
      </Trans>
    </span>
  );

  return (
    <InspectorSection title={t(d.sectionTitle)} headerAction={<HelpBubble content={d.sectionHelp} />}>
      <InspectorRow label={t`Включено`}>
        <ToggleRow>
          <UDCompactCheckbox
            aria-label={t`Включено`}
            checked={settings.enabled}
            onChange={(e) => commitSettings({ ...settings, enabled: e.target.checked })}
          />
          <HelpBubble content={enabledHelp} />
        </ToggleRow>
      </InspectorRow>
      <InspectorRow label={t`Ось`}>
        <FieldRow>
          <SelectField
            value={settings.axis}
            onChange={(v) => commitSettings({ ...settings, axis: v as MatchLayoutAxis })}
            options={axisOptions}
          />
          <HelpBubble content={axisHelp} />
        </FieldRow>
      </InspectorRow>
      <InspectorRow label={t`Выравнивание`}>
        <FieldRow>
          <SelectField
            value={settings.alignment}
            onChange={(v) => commitSettings({ ...settings, alignment: v as MatchLayoutAlignment })}
            options={alignmentOptions}
          />
          <HelpBubble content={alignmentHelp} />
        </FieldRow>
      </InspectorRow>
      <Divider />
      <InspectorRow label={t`Предпросмотр`}>
        <FieldRow>
          <CommitOptionalNumberField
            value={previewCount}
            placeholder={authoredCount > 0 ? `1..${authoredCount}` : '—'}
            min={1}
            max={authoredCount > 0 ? authoredCount : undefined}
            onCommit={(v) => setPreviewCount(clampPreview(v, authoredCount))}
          />
          <HelpBubble
            content={
              <span>
                <Trans>
                  Временно рендерит шаблон так, будто передано только N {previewUnitGen} — удобно для проверки выравнивания. Не
                  сохраняется. При выборе объекта предпросмотр сбрасывается.
                </Trans>
              </span>
            }
          />
        </FieldRow>
      </InspectorRow>
    </InspectorSection>
  );
};

export default CommonSlotLayoutSection;

function countAuthoredSlots(objects: readonly unknown[], scopeKey: LayoutScopeKey): number {
  let max = 0;
  for (const raw of objects) {
    const n = (raw as { data?: Record<string, number | undefined> } | undefined)?.data?.[scopeKey];
    if (typeof n === 'number' && n > max) max = n;
  }
  return max;
}

function clampPreview(value: number | undefined, authoredCount: number): number | undefined {
  if (value === undefined) return undefined;
  if (!Number.isFinite(value) || value < 1) return undefined;
  if (authoredCount <= 0) return undefined;
  if (value >= authoredCount) return undefined;
  return Math.floor(value);
}

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
`;

const FieldRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  min-width: 0;

  & > :first-child {
    flex: 1;
    min-width: 0;
  }

  & > :last-child {
    flex: 0 0 auto;
  }
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${theme.colors.DarkBlue100};
  margin: 4px 0;
`;
